import { useEffect, useState, useMemo } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Header } from "../components/Header";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { usePageTitle } from "../lib/usePageTitle";
import { Search, Star, Check, Loader2, Package, Palette, Sparkles, Download, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { id: "all", label: "Tout", icon: Sparkles },
  { id: "extension", label: "Extensions", icon: Package },
  { id: "theme", label: "Thèmes", icon: Palette },
];

export default function OmniStorePage() {
  usePageTitle("OmniStore");
  const { user, loading } = useAuth();
  const { themeId, theme, setTheme, reset } = useTheme();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [installed, setInstalled] = useState(new Set());
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);
  const [params] = useSearchParams();
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const c = params.get("c");
    if (c && ["extension", "theme"].includes(c)) setCat(c);
  }, [params]);

  useEffect(() => {
    api.get("/store/items").then(({ data }) => setItems(data.items || []));
    if (user && user !== false) {
      api.get("/store/installed").then(({ data }) => setInstalled(new Set(data.installed || [])));
    }
  }, [user]);

  const filtered = useMemo(() => {
    let r = items;
    if (cat !== "all") r = r.filter((it) => it.category === cat);
    if (q.trim()) {
      const ql = q.toLowerCase();
      r = r.filter((it) => it.name.toLowerCase().includes(ql) || it.description.toLowerCase().includes(ql));
    }
    return r;
  }, [items, cat, q]);

  const featured = items[0];

  const install = async (id) => {
    if (!user || user === false) { navigate("/login", { state: { from: "/store" } }); return; }
    setBusy(id);
    try {
      const item = items.find((it) => it.id === id);
      if (installed.has(id)) {
        await api.delete(`/store/install/${id}`);
        setInstalled((s) => { const n = new Set(s); n.delete(id); return n; });
        // If we just uninstalled the active theme, revert to default
        if (item?.category === "theme" && themeId === id) {
          reset();
          toast.success("Désinstallé · Thème par défaut restauré");
        } else {
          toast.success("Désinstallé");
        }
      } else {
        await api.post(`/store/install/${id}`);
        setInstalled((s) => new Set(s).add(id));
        // If it's a theme, also activate it immediately
        if (item?.category === "theme") {
          setTheme(id);
          toast.success(`Thème "${item.name}" activé !`);
        } else {
          toast.success("Installé !");
        }
      }
    } catch (e) {
      toast.error("Échec");
    } finally { setBusy(null); }
  };

  const activateTheme = (id) => {
    setTheme(id);
    const item = items.find((it) => it.id === id);
    toast.success(`Thème "${item?.name || id}" activé`);
  };

  if (loading) return null;

  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="store-page">
      <Header compact />
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-8">
        {/* Hero */}
        <div className="rounded-3xl bg-gradient-to-br from-[#1e1b4b] via-[#312e81] to-[#7c3aed] text-white p-8 sm:p-12 mb-8 relative overflow-hidden">
          <div className="absolute -right-12 -top-12 w-64 h-64 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -left-8 bottom-0 w-48 h-48 rounded-full bg-pink-500/20 blur-3xl" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur text-xs font-medium mb-4">
              <Sparkles size={12} /> OmniStore — extensions & thèmes officiels
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-3">
              Personnalisez votre <span className="bg-gradient-to-r from-amber-300 to-pink-300 bg-clip-text text-transparent">OmniSearch</span>
            </h1>
            <p className="text-white/80 max-w-xl text-sm sm:text-base">
              Extensions productivité, thèmes pour tous les styles. Tout est gratuit, sans tracker, et activable en un clic.
            </p>
            <div className="mt-6 relative max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Rechercher dans le store…"
                className="pl-11 bg-white/95 text-black rounded-full border-0 h-11"
                data-testid="store-search-input"
              />
            </div>
          </div>
        </div>

        {/* Category tabs */}
        <div className="flex items-center gap-2 mb-6 flex-wrap">
          {CATEGORIES.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setCat(id)}
              data-testid={`store-cat-${id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm border transition ${cat === id ? "bg-black text-white border-black" : "border-[var(--omni-border)] hover:bg-[var(--omni-surface)]"}`}
            >
              <Icon size={14} /> {label}
            </button>
          ))}
          {themeId !== "default" && (
            <div className="flex items-center gap-2 ml-2 px-3 py-2 rounded-full bg-gradient-to-r from-[var(--omni-accent)] to-purple-500 text-white text-xs font-medium" data-testid="store-active-theme-banner">
              <Palette size={12} /> Thème actif : <strong>{theme.name}</strong>
              <button onClick={reset} className="ml-1 hover:underline inline-flex items-center gap-1" data-testid="store-reset-theme">
                <RotateCcw size={11} /> Réinitialiser
              </button>
            </div>
          )}
          <span className="text-xs text-gray-400 ml-auto">{filtered.length} résultat{filtered.length > 1 ? "s" : ""}</span>
        </div>

        {/* Featured card */}
        {cat === "all" && featured && !q && (
          <div
            className="rounded-2xl border border-[var(--omni-border)] p-6 mb-8 grid md:grid-cols-[1fr_2fr] gap-6 items-center cursor-pointer hover:shadow-lg transition"
            onClick={() => setSelected(featured)}
            data-testid="store-featured"
          >
            <div className="aspect-video rounded-xl flex items-center justify-center text-7xl" style={{ background: `linear-gradient(135deg, ${featured.color}40, ${featured.color}10)` }}>
              {featured.icon}
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-wide text-amber-600 mb-2">★ Mise en avant</div>
              <h2 className="text-2xl sm:text-3xl font-black tracking-tight">{featured.name}</h2>
              <p className="text-sm text-gray-500 mt-2">{featured.description}</p>
              <div className="flex items-center gap-4 mt-4 text-sm">
                <div className="flex items-center gap-1 text-amber-500"><Star size={14} fill="currentColor" /> {featured.rating}</div>
                <div className="text-gray-500">{featured.users} utilisateurs</div>
                <div className="text-gray-400">par {featured.author}</div>
              </div>
            </div>
          </div>
        )}

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="store-grid">
          {filtered.map((it) => {
            const isInstalled = installed.has(it.id);
            const isActiveTheme = it.category === "theme" && themeId === it.id;
            return (
              <div
                key={it.id}
                className={`border rounded-2xl p-5 hover:shadow-md transition cursor-pointer flex flex-col ${isActiveTheme ? "border-[var(--omni-accent)] ring-2 ring-[var(--omni-accent)]/20" : "border-[var(--omni-border)]"}`}
                onClick={() => setSelected(it)}
                data-testid={`store-item-${it.id}`}
              >
                <div className="aspect-video rounded-xl flex items-center justify-center text-5xl mb-4 relative" style={{ background: `linear-gradient(135deg, ${it.color}40, ${it.color}10)` }}>
                  {it.icon}
                  {isActiveTheme && (
                    <span className="absolute top-2 left-2 bg-[var(--omni-accent)] text-white text-[10px] font-bold uppercase px-2 py-0.5 rounded-full inline-flex items-center gap-1" data-testid={`store-active-${it.id}`}>
                      <Check size={10} /> Actif
                    </span>
                  )}
                </div>
                <div className="flex items-start gap-2 mb-2">
                  <h3 className="font-bold text-base flex-1 leading-tight">{it.name}</h3>
                  {it.category === "theme" ? (
                    <span className="text-[10px] font-mono uppercase bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">Thème</span>
                  ) : (
                    <span className="text-[10px] font-mono uppercase bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Ext.</span>
                  )}
                </div>
                <p className="text-xs text-gray-500 line-clamp-2 mb-3">{it.description}</p>
                <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                  <div className="flex items-center gap-1 text-amber-500"><Star size={11} fill="currentColor" /> {it.rating}</div>
                  <div>{it.users}</div>
                </div>
                <div className="flex gap-1 mt-auto">
                  <Button
                    onClick={(e) => { e.stopPropagation(); install(it.id); }}
                    disabled={busy === it.id}
                    size="sm"
                    className={`rounded-full flex-1 ${isInstalled ? "bg-emerald-600 hover:bg-red-600 text-white" : "bg-black hover:bg-gray-800 text-white"}`}
                    data-testid={`store-install-${it.id}`}
                  >
                    {busy === it.id ? <Loader2 className="animate-spin" size={14} /> : isInstalled ? <><Check size={13} className="mr-1" /> Installé</> : <><Download size={13} className="mr-1" /> Installer</>}
                  </Button>
                  {it.category === "theme" && isInstalled && !isActiveTheme && (
                    <Button
                      onClick={(e) => { e.stopPropagation(); activateTheme(it.id); }}
                      size="sm"
                      variant="outline"
                      className="rounded-full"
                      data-testid={`store-activate-${it.id}`}
                      title="Activer ce thème"
                    >
                      <Palette size={13} />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-16 text-gray-400">Aucun résultat pour cette recherche.</div>
        )}
      </main>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)} data-testid="store-detail">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="aspect-[2/1] flex items-center justify-center text-9xl rounded-t-2xl" style={{ background: `linear-gradient(135deg, ${selected.color}40, ${selected.color}10)` }}>
              {selected.icon}
            </div>
            <div className="p-6 sm:p-8">
              <div className="flex items-start gap-4 mb-3">
                <h2 className="text-2xl sm:text-3xl font-black tracking-tight flex-1">{selected.name}</h2>
                <Button
                  onClick={() => install(selected.id)}
                  disabled={busy === selected.id}
                  className={`rounded-full ${installed.has(selected.id) ? "bg-emerald-600 hover:bg-red-600 text-white" : "bg-black text-white"}`}
                  data-testid="store-detail-install"
                >
                  {busy === selected.id ? <Loader2 className="animate-spin" size={14} /> : installed.has(selected.id) ? <><Check size={14} className="mr-1" /> Installé</> : <><Download size={14} className="mr-1" /> Installer</>}
                </Button>
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-600 mb-6">
                <div className="flex items-center gap-1 text-amber-500"><Star size={14} fill="currentColor" /> {selected.rating}</div>
                <span>•</span>
                <span>{selected.users} utilisateurs</span>
                <span>•</span>
                <span>par {selected.author}</span>
              </div>
              <p className="text-gray-700 leading-relaxed">{selected.description}</p>
              <div className="grid grid-cols-3 gap-3 mt-6">
                {selected.screenshots.map((c, i) => (
                  <div key={i} className="aspect-video rounded-lg" style={{ background: c }} />
                ))}
              </div>
              <div className="flex justify-end mt-6">
                <button onClick={() => setSelected(null)} className="text-sm text-gray-500 hover:text-black">Fermer</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
